import { WCS } from './../../components/ui/calendar/calendar-week/week-utils';
import {
  addDays,
  differenceInMinutes,
  endOfDay,
  format,
  getDay,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { ICalendarEvent, ICalendarEvents } from '../../components/ui/calendar/calendars';
import { EVisitItem, TExistingEVisitRule } from '../../types/e-visits';
import getDaysArray from '../../lib/utils/get-days-array';
import moment from 'moment';

const EmptyEVisitRule = {
  id: undefined,
  description: '',
  toDate: undefined,
  price: 0,
  blockSize: WCS.minSelectInterval,
} as const;

function generateEmptyTerminatingEvent(
  rule: TExistingEVisitRule,
  type: 'start' | 'end',
): Omit<TExistingEVisitRule, 'id'> & { id: undefined } {
  const base = {
    ...rule,
    ...EmptyEVisitRule,
    fromDate: startOfDay(new Date(rule.fromTime)).toISOString(),
  };

  if (type === 'start') {
    return {
      ...base,
      fromTime: startOfDay(new Date(rule.fromTime)).toISOString(),
      toTime: rule.fromTime,
    };
  } else {
    return {
      ...base,
      fromTime: rule.toTime,
      toTime: endOfDay(new Date(rule.toTime)).toISOString(),
    };
  }
}

function generateEmptyEvent(date: Date, medicalInstitutionId?: string, expertiseCode?: string) {
  const start = startOfDay(date).toISOString();
  const end = endOfDay(date).toISOString();
  return {
    start,
    end,
    eventData: {
      ...EmptyEVisitRule,
      medicalInstitutionId,
      expertiseCode,
      days: [getDay(date)],
      fromTime: start,
      toTime: end,
      fromDate: start,
      tag: null,
    } as TGeneratedEvent,
  };
}

export type TGeneratedEvent = TExistingEVisitRule | Omit<TExistingEVisitRule, 'id'>;

function isOnlyFuture(date: Date) {
  return isBefore(date, startOfDay(Date.now()));
}

/**
 * Function to generate empty event between existing
 * @param rules existing rules
 * @param type generation type
 * @returns new events
 */
export function generateRulesAddArray(
  rules: { [date: string]: TExistingEVisitRule[] },
  {
    type = 'all',
    medicalInstitutionId,
    expertiseCode,
    weekStart,
  }: {
    type: 'all' | 'only-added' | 'only-future';
    medicalInstitutionId?: string;
    expertiseCode?: string;
    weekStart: Date;
  },
) {
  /**
   * If we don't have rules, generate for all day for current week
   */
  if (!rules || Object.keys(rules).length === 0) {
    return getDaysArray<number>(0, 'number').reduce((acc, d) => {
      const date = addDays(weekStart, d);

      if (type === 'only-future' && isOnlyFuture(date)) {
        return acc;
      }

      acc[format(date, 'yyyy-MM-dd')] = [
        generateEmptyEvent(date, medicalInstitutionId, expertiseCode),
      ];

      return acc;
    }, {} as ICalendarEvents<any>);
  }

  const result: ICalendarEvents<TExistingEVisitRule | undefined> = {};

  const existingDates = Object.keys(rules);
  const days = getDaysArray<number>(0, 'number');

  /**
   * For each day generate whole day or
   * Terminating events and Intermediate events
   */
  for (const day of days) {
    const date = addDays(weekStart, day);
    const skip = type === 'only-future' && isOnlyFuture(date);

    const key = existingDates.find((k) => isSameDay(new Date(k), date));
    const dayRules = key ? rules[key] : undefined;
    const resultRules: ICalendarEvent<TGeneratedEvent>[] = [];
    const groups = groupOverlappingRules(dayRules);

    if (groups.length > 0) {
      const firstGroup = groups[0];
      const lastGroup = groups[groups.length - 1];
      /**
       * If group starts in middle of day, add one from midnight
       */
      if (!skip && differenceInMinutes(new Date(firstGroup.start), startOfDay(date)) > 0) {
        const start = startOfDay(date).toISOString();
        const end = new Date(firstGroup.start).toISOString();
        resultRules.push({
          start,
          end,
          eventData: generateEmptyTerminatingEvent(firstGroup.rules[0], 'start'),
          minTime: start,
          maxTime: end,
        });
      }

      /**
       * Go through all groups and add events
       */
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        const beforeGroup = groups[i - 1];
        const nextGroup = groups[i + 1];

        for (const rule of group.rules) {
          resultRules.push({
            start: rule.fromTime,
            end: rule.toTime,
            eventData: rule,
            minTime: beforeGroup?.end || startOfDay(date).toISOString(),
            maxTime: nextGroup?.start || endOfDay(date).toISOString(),
          });
        }

        /**
         * If pair of groups have time hole, generate empty event
         */
        if (
          !skip &&
          nextGroup &&
          differenceInMinutes(new Date(nextGroup.start), new Date(group.end))
        ) {
          const start = group.end;
          const end = nextGroup.start;

          resultRules.push({
            start,
            end,
            eventData: {
              ...group.rules[group.rules.length - 1],
              ...EmptyEVisitRule,
              fromTime: start,
              toTime: end,
            },
            minTime: start,
            maxTime: end,
          });
        }
      }

      /**
       * If group ends in middle of day add one until midnight
       */
      if (!skip && differenceInMinutes(endOfDay(date), new Date(lastGroup.start)) > 0) {
        const start = lastGroup.end;
        const end = endOfDay(date).toISOString();
        resultRules.push({
          start,
          end,
          eventData: generateEmptyTerminatingEvent(
            lastGroup.rules[lastGroup.rules.length - 1],
            'end',
          ),
          minTime: start,
          maxTime: end,
        });
      }
      result[format(date, 'yyyy-MM-dd')] = resultRules as any;
    } else {
      if (!skip) {
        result[format(date, 'yyyy-MM-dd')] = [
          generateEmptyEvent(date, medicalInstitutionId, expertiseCode),
        ] as any;
      } else {
        result[format(date, 'yyyy-MM-dd')] = [] as any;
      }
    }
  }

  return result;
}

/**
 * Function to check if whole interval is used without empty space
 * @param start start date
 * @param end end date
 * @param blockSize size of block
 * @returns info
 */
export function fitAllBlocksInInterval(start: Date, end: Date, blockSize?: number) {
  const diff = blockSize ? Math.abs(differenceInMinutes(start, end)) / blockSize : 1;
  const fullBlocks = Math.floor(diff);
  const emptySpace = diff % 1 !== 0;

  return {
    fullBlocks,
    emptySpace,
  };
}

/**
 * Sort rule by start time
 * @param rules rules to dort
 * @returns sorted rules copy
 */
function sortRulesByStart(rules: TExistingEVisitRule[]) {
  return [...rules].sort((a, b) =>
    a.fromTime < b.fromTime ? -1 : a.fromTime > b.fromTime ? 1 : 0,
  );
}

/**
 * Find event with last ending time
 * @param rules rules to search through
 * @returns event last has latest ending time
 */
function findLastRule(rules: TExistingEVisitRule[]) {
  let last: TExistingEVisitRule | undefined = undefined;

  for (const rule of rules) {
    if (!last || rule.toTime > last.toTime) {
      last = rule;
    }
  }
  return last;
}

/**
 * Group overlapping events to groups, and find group start/end times
 * @param rules rules to group
 * @returns grouped rules
 */
function groupOverlappingRules(rules?: TExistingEVisitRule[]) {
  const groups: { start: string; end: string; rules: TExistingEVisitRule[] }[] = [];

  if (!rules) {
    return groups;
  }

  const events = sortRulesByStart(rules);

  while (events.length > 0) {
    const e = events.shift();

    // Find overlapping events.
    const overlappingEvents: TExistingEVisitRule[] = [e!];
    while (
      events.length > 0 &&
      e!.fromTime <= events[0].fromTime &&
      e!.toTime > events[0].fromTime
    ) {
      overlappingEvents.push(events.shift()!);
    }

    groups.push({
      start: overlappingEvents[0].fromTime,
      end: findLastRule(overlappingEvents)?.toTime!,
      rules: overlappingEvents,
    });
  }

  return groups;
}

export function getEventNameAndDate(item: EVisitItem) {
  let name;
  let date = `${moment(item.event.from).format('HH:mm')} - ${moment(item.event.to).format(
    'HH:mm',
  )}`;

  if (item.order !== undefined && item.order.patientProfile) {
    name = `${item.order.patientProfile.firstName} ${item.order?.patientProfile.lastName}`;
  } else {
    name = item.event.title || date;
  }

  if (name === date) {
    date = '';
  }

  return { name, date };
}
